import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Paper,
  CircularProgress,
  Box,
  Typography,
  Avatar,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Alert,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { alpha, useTheme } from "@mui/material/styles";
import { useAuth } from "../context/studentAuthContext";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function TutorDetails() {
  const { student } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();

  const [tutor, setTutor] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  useEffect(() => {
    const fetchTutor = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/admin/landing-tutors/${id}`);
        setTutor(res.data);
      } catch (err) {
        console.error("Fetch tutor error:", err);
        setSnackbar({
          open: true,
          message: "Failed to load tutor details. Please try again.",
          severity: "error",
        });
      }
    };
    fetchTutor();
  }, [id]);

  const handleApply = (e) => {
    if (!student) {
      e.preventDefault();
      setSnackbar({
        open: true,
        message: "Please login to apply for courses.",
        severity: "warning",
      });
      setTimeout(() => navigate("/login"), 1500);
    }
  };

  if (!tutor)
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          p: 4,
        }}
      >
        <CircularProgress size={50} sx={{ mb: 2, color: theme.palette.primary.main }} />
        <Typography variant="h6" color="text.secondary">
          Loading tutor details...
        </Typography>
      </Box>
    );

  return (
    <>
      <Box sx={{ p: { xs: 2, md: 6 }, mt: 8, maxWidth: 900, mx: "auto" }}>
        {/* Tutor Info Card */}
        <Paper
          elevation={4}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            mb: 5,
            textAlign: "center",
            backgroundColor: "#fff",
            boxShadow: "0 8px 35px rgba(0,0,0,0.12)",
          }}
        >
          <Avatar
            src={
              tutor.profile_img
                ? `${BASE_URL}/uploads/tutors/${tutor.profile_img}`
                : "/images/default-avatar.png"
            }
            sx={{
              width: 120,
              height: 120,
              mx: "auto",
              mb: 3,
              boxShadow: "0 6px 18px rgba(0,0,0,0.1)",
              border: `4px solid ${alpha(theme.palette.primary.main, 0.4)}`,
            }}
          />

          <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
            {tutor.tutor_name}
          </Typography>

          <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
            Course: {tutor.name || "General Course"}
          </Typography>

          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.7, color: "#475569" }}>
            {tutor.course_description ||
              "Detailed course description coming soon. Get ready to learn from the best!"}
          </Typography>

          <Box sx={{ mb: 3, fontSize: "1.4rem", fontWeight: 700, color: theme.palette.primary.main }}>
            Price: ₦{Number(tutor.price || 0).toLocaleString()}
          </Box>

          {/* Enroll Button aligned with primary main */}
          <Button
            variant="contained"
            component={Link}
            to="/enroll"
            state={{ courseId: tutor.course_id, tutorId: tutor.id, step: 4 }}
            onClick={handleApply}
            size="large"
            sx={{
              px: 5,
              py: 1.4,
              borderRadius: 3,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "1rem",
              backgroundColor: theme.palette.primary.main,
              color: "#fff",
              boxShadow: `0 4px 15px ${alpha(theme.palette.primary.main, 0.25)}`,
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
                transform: "translateY(-2px)",
              },
              transition: "all 0.3s ease",
            }}
          >
            Enroll in this Course
          </Button>
        </Paper>

        {/* How to Apply Section */}
        <Paper
          elevation={1}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 4,
            backgroundColor: "#f8fafc",
          }}
        >
          <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
            How to Apply for This Course
          </Typography>

          <Typography sx={{ mb: 3, color: "text.secondary" }}>
            Follow these simple steps to start your learning journey:
          </Typography>

          <List>
            {[
              "Click the Enroll button above.",
              "Login or create an account if you don't have one.",
              "Select the category and course you want to apply for.",
              "Choose Mode of lecture (Onsite/Online).",
              "Select preferred tutor based on reviews and fee.",
              "Make payment using any available payment method.",
              "Receive confirmation and learning materials immediately.",
            ].map((step, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <CheckCircleIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary={step} />
              </ListItem>
            ))}
          </List>
        </Paper>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}